import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';

const EMPTY_FORM_OPTIONS = {
  partnerAdoptionEngineers: [],
  tierType: [],
  serviceLevel: [],
};

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

export const usePartnerCompanyFormOptions = (enabled = true) => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [formOptions, setFormOptions] = useState(EMPTY_FORM_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFormOptions = useCallback(async () => {
    if (!enabled) {
      setFormOptions(EMPTY_FORM_OPTIONS);
      setLoading(false);
      return;
    }

    const token = resolveAccessToken(accessToken);
    if (!token) {
      setFormOptions(EMPTY_FORM_OPTIONS);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchOptions = async (activeToken) => {
      const data = await partnerPortalApi.fetchPartnerCompanyFormOptions(activeToken);
      setFormOptions({
        partnerAdoptionEngineers: data?.partnerAdoptionEngineers || [],
        tierType: data?.tierType || [],
        serviceLevel: data?.serviceLevel || [],
      });
    };

    try {
      await fetchOptions(token);
    } catch (err) {
      if (isUnauthorizedError(err) && Cookies.get('refreshToken')) {
        try {
          await dispatch(refreshToken());
          const refreshedToken = Cookies.get('accessToken');
          if (refreshedToken) {
            await fetchOptions(refreshedToken);
            return;
          }
        } catch (refreshError) {
          setError(refreshError.message || err.message);
          setFormOptions(EMPTY_FORM_OPTIONS);
          return;
        }
      }

      setError(err.message);
      setFormOptions(EMPTY_FORM_OPTIONS);
    } finally {
      setLoading(false);
    }
  }, [accessToken, dispatch, enabled]);

  useEffect(() => {
    loadFormOptions();
  }, [loadFormOptions]);

  return {
    partnerAdoptionEngineers: formOptions.partnerAdoptionEngineers,
    tierTypeOptions: formOptions.tierType,
    serviceLevelOptions: formOptions.serviceLevel,
    loading,
    error,
    refreshFormOptions: loadFormOptions,
  };
};
